import { Box, IconButton, Drawer, Stack } from '@mui/material';
import { useAppDispatch, useAppSelector, type RootState } from '@libs/redux/store';
import { AppLanguage, toggleDrawer } from '@libs/redux/appUiSlice';
import CloseIcon from '@mui/icons-material/Close';
import { useIntl } from 'react-intl';
import { useAuth0 } from '@auth0/auth0-react';
import { useAppLocation } from '@shared/reactRouter';
import { getMenuItems, SideMenuList } from '@shared/components/SideMenuList';
import { usePersistentFilters, useSubscriptionPlan } from '@shared//store';
import { useSuspenseTeamMemberQuery } from '@features/team/queries';
import { useSuspenseStocksQuery } from '@features/stock/queries';
import { useIsEnabledForUsers } from '@shared/auth/hooks';
import { usePendingReservationsQuery } from '@features/pending-reservations/queries';

const SideMenu = () => {
  const isOpen = useAppSelector((state: RootState) => state.appUi.isDrawerOpen);
  const dispatch = useAppDispatch();
  const intl = useIntl();
  const { data: teamMember } = useSuspenseTeamMemberQuery();
  const isEnabledForUsers = useIsEnabledForUsers();

  const { data: stocks } = useSuspenseStocksQuery();
  const [filters, updateFilters] = usePersistentFilters();
  const { logout, user } = useAuth0();
  const location = useAppLocation();
  const plan = useSubscriptionPlan();
  const { data: pendingReservations } = usePendingReservationsQuery();
  const pendingCount = pendingReservations?.filter(r => r.status === 'PENDING').length || 0;

  const handleDrawerToggle = () => {
    dispatch(toggleDrawer());
  };

  const toggleLanguage = () => {
    updateFilters(draft => {
      const languages: AppLanguage[] = ['cs', 'en', 'uk', 'de', 'sk'];
      const currentIndex = languages.indexOf(filters.language);
      draft.language = languages[(currentIndex + 1) % languages.length];
    });
    window.location.reload();
    dispatch(toggleDrawer());
  };

  const handleLogout = () => {
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('@@auth0spajs@@')) {
        localStorage.removeItem(key);
      }
    });

    logout({
      logoutParams: {
        returnTo: `${window.location.origin}/hairdresser`,
      },
    });
  };

  const menuItems = getMenuItems(
    user?.sub,
    intl,
    plan,
    isEnabledForUsers,
    teamMember,
    stocks,
    filters,
    toggleLanguage,
    handleLogout,
    pendingCount || undefined,
  );

  const filteredMenuItems = menuItems.filter(item => item.hasFeatureAccess);

  return (
    <Drawer anchor="left" open={isOpen} onClose={handleDrawerToggle}>
      <Stack sx={{ width: 250 }} role="presentation">
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', padding: '8px' }}>
          <IconButton
            id="side-menu-close-button"
            onClick={handleDrawerToggle}
            aria-label="close drawer"
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <SideMenuList items={filteredMenuItems} onClose={handleDrawerToggle} location={location} />
      </Stack>
    </Drawer>
  );
};

export default SideMenu;
